import { Injectable, computed, effect, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { filter, map } from 'rxjs';
import { TabViewItem } from '@interfaces/a-organizar/tabViewItem';
import { PartnerIncomeTaxStateService } from '@features/dashboard/performance/pages/dfp/components/partner-income-tax/partner-income-tax-state.service';
import { DfpModuleKey, DfpModulesState } from './dfp-modules.state';

/**
 * Abas da Análise Econômica. O valor é o que vai no `?tab=` da URL (em inglês, regra do card 3307). Desde o card
 * 3801 Destaques e Faturamento deixaram de ser abas: viraram módulos da aba de módulos (`DfpModulesState`).
 * Card 3824: a aba de módulos passou a se chamar Resumo (o valor `statements` continua, para não quebrar link) e
 * ganhou ao lado uma aba para cada demonstrativo completo. PBI 3875 (Victor, 03/11/2026): as abas de demonstrativo
 * viraram uma só, Demonstrativos (`financial-statements`), com Balanço, DRE e Faturamento um embaixo do outro, cada um
 * recolhível pelo título.
 */
export type DfpStatementKey = Extract<DfpModuleKey, 'balance' | 'income' | 'cashflow' | 'revenue'>;
export type DfpTabKey = 'statements' | 'financial-statements' | 'partner-income-tax';

/** Comando de recolher/expandir todos os painéis; `seq` muda a cada clique, para o mesmo comando valer de novo. */
export interface DfpCollapseCommand {
  collapsed: boolean;
  seq: number;
}

export const DFP_TAB_QUERY_PARAM = 'tab';
export const DFP_DEFAULT_TAB: DfpTabKey = 'statements';
export const DFP_STATEMENTS_TAB: DfpTabKey = 'financial-statements';

/**
 * Demonstrativos da aba Demonstrativos, empilhados nesta ordem: Balanço, DRE, DFC, Faturamento. A DFC entrou no PBI
 * 3875 (antes só existia na modelagem).
 */
export const DFP_STATEMENTS: readonly DfpStatementKey[] = [
  'balance',
  'income',
  'cashflow',
  'revenue',
];

/**
 * Links antigos (`?tab=highlights`, card 3515): abrem o Resumo com o módulo ligado, sem mudar a preferência gravada de
 * quem abriu.
 */
export const DFP_LEGACY_TAB_MODULES: Readonly<Record<string, DfpModuleKey>> = {
  highlights: 'highlights',
};

const TAB_TITLES: Record<DfpTabKey, string> = {
  statements: 'Resumo',
  'financial-statements': 'Demonstrativos',
  'partner-income-tax': 'IR dos sócios',
};

/**
 * Módulos que a tela fixa (empilhados em largura total, sem a barra "Mostrar"): os demonstrativos na aba
 * Demonstrativos, ou `null` no Resumo e no IR dos sócios.
 */
export function statementTabModules(tab: DfpTabKey): readonly DfpStatementKey[] | null {
  return tab === DFP_STATEMENTS_TAB ? DFP_STATEMENTS : null;
}

/** Abas visíveis, na ordem: a de IR dos sócios só com a permissão e fora do modo somente leitura. */
export function visibleDfpTabs(showPartnerIncomeTax: boolean): DfpTabKey[] {
  const tabs: DfpTabKey[] = ['statements', DFP_STATEMENTS_TAB];
  return showPartnerIncomeTax ? [...tabs, 'partner-income-tax'] : tabs;
}

/** Lê o `?tab=`: valor desconhecido, antigo ou aba indisponível cai na primeira. */
export function resolveDfpTab(value: unknown, available: readonly DfpTabKey[]): DfpTabKey {
  return available.includes(value as DfpTabKey) ? (value as DfpTabKey) : DFP_DEFAULT_TAB;
}

/** Módulo que um `?tab=` antigo pede para ligar no Resumo (`null` quando não é um valor antigo). */
export function legacyDfpTabModule(value: unknown): DfpModuleKey | null {
  return typeof value === 'string' ? (DFP_LEGACY_TAB_MODULES[value] ?? null) : null;
}

/**
 * `?tab=` antigo do card 3824 (`balance`, `income`, `revenue`, uma aba por demonstrativo), que agora abre a aba
 * Demonstrativos. `null` quando não é um desses valores.
 */
export function legacyDfpStatementTab(value: unknown): DfpStatementKey | null {
  return DFP_STATEMENTS.includes(value as DfpStatementKey) ? (value as DfpStatementKey) : null;
}

/** Itens do `app-tab-view`; o contador só na aba de IR dos sócios (número de sócios). */
export function buildDfpTabItems(tabs: readonly DfpTabKey[], partnerCount: number): TabViewItem[] {
  return tabs.map((key, index) => ({
    title: TAB_TITLES[key],
    tabindex: index,
    content: '',
    id: key,
    count: key === 'partner-income-tax' && partnerCount > 0 ? partnerCount : undefined,
  }));
}

/**
 * Aba ativa da Análise Econômica, com `?tab=` na URL. Provido pelo `DfpComponent` para ele não crescer mais.
 * Embutida (aba do deal), a URL é de quem embute: a aba não é lida nem gravada nela.
 */
@Injectable()
export class DfpTabsState {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  /** Diz se a aba de IR existe (permissão e modo) e quantos sócios há, para o contador. */
  private readonly partnerIncomeTax = inject(PartnerIncomeTaxStateService);
  /** Módulos do Resumo: o `?tab=` antigo liga o módulo correspondente (card 3801). */
  private readonly modules = inject(DfpModulesState);

  /** Aba pedida (URL ou clique). A ativa de fato depende de a aba estar disponível. */
  private readonly requested = signal<DfpTabKey>(DFP_DEFAULT_TAB);
  private embedded = false;

  /**
   * "Expandir todos" / "Recolher todos" da aba Demonstrativos (pedido do Victor, 03/11/2026). Cada clique gera um comando
   * novo (`seq`), que os painéis aplicam por cima do estado de cada um; o título de cada painel continua recolhendo só ele.
   */
  readonly collapseCommand = signal<DfpCollapseCommand | null>(null);

  readonly tabs = computed(() => visibleDfpTabs(this.partnerIncomeTax.available()));
  readonly active = computed(() => resolveDfpTab(this.requested(), this.tabs()));
  readonly activeIndex = computed(() => this.tabs().indexOf(this.active()));
  /** Embutida (aba do deal): a URL é de quem embute, então as abas não viram link. */
  private readonly embeddedMode = signal(false);

  /** URL atual, para o link de cada aba levar a empresa e os demais parâmetros da tela. */
  private readonly currentUrl = toSignal(
    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd),
      map(() => this.router.url)
    ),
    { initialValue: this.router.url }
  );

  /**
   * Itens do `app-tab-view`, cada um com o endereço da aba (`href`): botão direito "Abrir em nova guia" e Ctrl+clique
   * abrem a aba em outra guia (pedido do Victor, 03/11/2026).
   */
  readonly items = computed(() => {
    this.currentUrl();
    const items = buildDfpTabItems(this.tabs(), this.partnerIncomeTax.partnerCount());
    if (this.embeddedMode()) return items;
    return items.map(item => ({ ...item, href: this.tabHref(item.id as DfpTabKey) }));
  });

  constructor() {
    // A aba Demonstrativos usa as mesmas tabelas do Resumo (a exportação Excel lê as do DOM): só troca os módulos
    // exibidos e empilha a grade, sem mexer na preferência gravada da barra "Mostrar" (cards 3824 e 3875).
    effect(() => this.modules.setFixedModules(statementTabModules(this.active())));
  }

  /** Lê a aba da URL (fora do modo embutido). A aba de IR pedida antes de a permissão chegar fica guardada. */
  init(embedded: boolean): void {
    this.embedded = embedded;
    this.embeddedMode.set(embedded);
    if (embedded) return;
    const value = this.route.snapshot.queryParamMap.get(DFP_TAB_QUERY_PARAM);
    if (!value) return;

    const legacyModule = legacyDfpTabModule(value);
    if (legacyModule) {
      this.modules.ensureOn(legacyModule, { persist: false });
      // Tira o valor antigo da URL: o Resumo é a aba padrão e não vai no `?tab=`.
      this.writeUrl(null);
      return;
    }

    // `?tab=balance|income|revenue` (uma aba por demonstrativo, card 3824): abre Demonstrativos.
    if (legacyDfpStatementTab(value)) {
      this.requested.set(DFP_STATEMENTS_TAB);
      this.writeUrl(DFP_STATEMENTS_TAB);
      return;
    }

    this.requested.set(resolveDfpTab(value, visibleDfpTabs(true)));
  }

  selectIndex(index: number): void {
    const key = this.tabs()[index];
    if (!key || key === this.active()) return;
    this.requested.set(key);
    if (this.embedded) return;
    this.writeUrl(key === DFP_DEFAULT_TAB ? null : key);
  }

  /** Endereço da tela com a aba pedida (o Resumo, padrão, sai sem `?tab=`), mantendo empresa e demais parâmetros. */
  private tabHref(tab: DfpTabKey): string {
    return this.router.serializeUrl(
      this.router.createUrlTree([], {
        relativeTo: this.route,
        queryParams: {
          [DFP_TAB_QUERY_PARAM]: tab === DFP_DEFAULT_TAB ? null : tab,
          statement: null,
        },
        queryParamsHandling: 'merge',
      })
    );
  }

  /** Recolhe ou expande de uma vez todos os demonstrativos da aba Demonstrativos. */
  setAllCollapsed(collapsed: boolean): void {
    this.collapseCommand.set({ collapsed, seq: (this.collapseCommand()?.seq ?? 0) + 1 });
  }

  is(key: DfpTabKey): boolean {
    return this.active() === key;
  }

  /**
   * Aba pedida (URL ou clique), mesmo antes de a permissão de IR chegar. A DFP usa para adiar os demonstrativos
   * quando a tela abre direto na aba IR dos sócios.
   */
  isRequested(key: DfpTabKey): boolean {
    return this.requested() === key;
  }

  /**
   * Grava a aba na URL sem criar entrada no histórico (`null` tira o parâmetro). Tira também o `?statement=` do
   * seletor que existiu no PBI 3875 antes de virar a lista empilhada.
   */
  private writeUrl(tab: DfpTabKey | null): void {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { [DFP_TAB_QUERY_PARAM]: tab, statement: null },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }
}
